import { defineMessages } from 'react-intl.macro';

import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { useDebouncedCallback } from 'use-debounce';

import { useSelector } from '@tmapy/redux';
import { HTTP_STATUS_OK } from '@tmapy/beetlejuice-utils';
import { Autocomplete, FormItem, FormRow, Input } from '@tmapy/style-guide';

import { callApi } from '../../utils/api';
import { getAddress, getCity, IAddressItem } from '../../utils/ruian';
import { RuianServiceResponse } from '../../types';

export const addressMessages = defineMessages({
  street: {
    id: 'addressautocomplete.street.label',
    defaultMessage: 'Ulice, č. p.',
  },
  city: {
    id: 'addressautocomplete.city.label',
    defaultMessage: 'Obec',
  },
  psc: {
    id: 'addressautocomplete.psc.label',
    defaultMessage: 'PSČ',
  },
});

export const AddressAutocomplete: React.FC<{
  streetIdent: string;
  streetValue: string;
  isStreetDisabled?: boolean;
  cityIdent: string;
  cityValue: string;
  isCityDisabled?: boolean;
  postalCodeIdent: string;
  postalCodeValue: string;
  isPostalCodeDisabled?: boolean;
  onChange(e: React.MouseEvent | React.ChangeEvent, data: { ident: string; value: string }[]): void;
}> = React.memo(
  ({
    streetIdent,
    streetValue,
    isStreetDisabled,
    cityIdent,
    cityValue,
    isCityDisabled,
    postalCodeIdent,
    postalCodeValue,
    isPostalCodeDisabled,
    onChange,
  }) => {
    const intl = useIntl();
    const cancelToken = axios.CancelToken.source();
    const services = useSelector((state) => state.app.services);
    const [addressItems, setAddressItems] = useState<IAddressItem[]>([]);
    const [street, setStreet] = useState<string>(streetValue);
    const [city, setCity] = useState<string>(cityValue);
    const [postal_code, setPostalCode] = useState<string>(postalCodeValue);

    const fetchData = useDebouncedCallback(async (value: string) => {
      const respond = await callApi<RuianServiceResponse>(
        `${services.suggestAddress}&singleLine=${value}`,
        'GET',
        undefined,
        undefined,
        cancelToken,
      );

      if (respond.status === HTTP_STATUS_OK) {
        let addressItems: IAddressItem[] = [];
        if (respond.data?.candidates?.length) {
          addressItems = respond.data.candidates.map(
            (item) =>
              ({
                address: item.address,
                city: item.attributes.extra.obecNazev,
                cityPart: item.attributes.extra.castObceNazev,
                street: item.attributes.extra.uliceNazev,
                postal_code: item.attributes.extra.psc,
                houseNumber: item.attributes.extra.cisloDomovni,
                orientationNumber: item.attributes.extra.cisloOrientacni,
              }) as IAddressItem,
          );
        }
        setAddressItems(addressItems);
      }
    }, 300);

    const handleSuggestionClick = (
      e: React.MouseEvent,
      eventData: { item: IAddressItem; name: string },
    ) => {
      const address = getAddress(eventData.item);
      const city = getCity(eventData.item);
      onChange(e, [
        { ident: streetIdent, value: address },
        { ident: cityIdent, value: city },
        { ident: postalCodeIdent, value: eventData.item.postal_code || '' },
      ]);

      setCity(city);
      setStreet(address);
      setPostalCode(eventData.item.postal_code || '');
    };

    const handleAutocompleteChange = (e: React.ChangeEvent, eventData: { value: string }) => {
      cancelToken.cancel('');
      fetchData(eventData.value);
      onChange(e, [{ ident: streetIdent, value: eventData.value }]);
      setStreet(eventData.value);
    };

    const handleInputCityChange = (
      e: React.ChangeEvent | React.MouseEvent,
      eventData: { name: string; value: string },
    ) => {
      onChange(e, [{ ident: eventData.name, value: eventData.value }]);
      setCity(eventData.value);
    };

    const handleInputPostalCodeChange = (
      e: React.ChangeEvent | React.MouseEvent,
      eventData: { name: string; value: string },
    ) => {
      onChange(e, [{ ident: eventData.name, value: eventData.value }]);
      setPostalCode(eventData.value);
    };

    const renderAddressItem = (item: { address: string }) => <>{item.address}</>;

    // When the component goes to be unmounted, we will cancel fetching data.
    useEffect(
      () => () => {
        fetchData.cancel();
      },
      [fetchData],
    );

    return (
      <FormRow>
        <FormItem label={intl.formatMessage(addressMessages.street)}>
          <Autocomplete
            name={streetIdent}
            value={street}
            items={addressItems}
            isDisabled={isStreetDisabled}
            renderItem={renderAddressItem}
            onChange={handleAutocompleteChange}
            onItemClick={handleSuggestionClick}
          />
        </FormItem>
        <FormItem label={intl.formatMessage(addressMessages.city)}>
          <Input
            name={cityIdent}
            autoComplete='city'
            value={city}
            isDisabled={isCityDisabled}
            onChange={handleInputCityChange}
          />
        </FormItem>
        <FormItem label={intl.formatMessage(addressMessages.psc)} characterCount={6}>
          <Input
            name={postalCodeIdent}
            autoComplete='zip-code'
            value={postal_code}
            isDisabled={isPostalCodeDisabled}
            onChange={handleInputPostalCodeChange}
          />
        </FormItem>
      </FormRow>
    );
  },
);
